import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";

const ShopEdit = ({ open, onClose, editData, token }) => {
  const { t, i18n } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate("shop/shop", token);
  const auth = useAuth();
  const currentUser = auth?.user?.user || auth?.user;
  const isSuperAdmin = Boolean(currentUser?.is_superuser);
  const currentLanguage = (
    i18n.resolvedLanguage ||
    i18n.language ||
    localStorage.getItem("i18nextLng") ||
    "en"
  ).split("-")[0];
  const isRTL = ["ps", "dr"].includes(currentLanguage);
  const fieldDirection = isRTL ? "rtl" : "ltr";
  const fieldTextAlign = isRTL ? "right" : "left";
  const rtlTextFieldProps = {
    inputProps: {
      dir: fieldDirection,
      style: { textAlign: fieldTextAlign },
    },
    InputLabelProps: {
      sx: {
        direction: fieldDirection,
        right: isRTL ? 24 : "auto",
        left: isRTL ? "auto" : undefined,
        transformOrigin: isRTL ? "top right" : "top left",
      },
    },
    sx: {
      direction: fieldDirection,
      "& .MuiOutlinedInput-root": {
        direction: fieldDirection,
      },
      "& .MuiInputBase-input": {
        textAlign: fieldTextAlign,
      },
      "& .MuiInputLabel-root": {
        textAlign: fieldTextAlign,
      },
    },
  };

  const { data: usersData, isLoading: isUsersLoading } = useFetchObjects(
    ["shop-assigned-users"],
    "admin/users/?page_size=200",
    token,
    { enabled: isSuperAdmin && open }
  );

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ownerId, setOwnerId] = useState("");

  const users = usersData?.results || usersData || [];

  useEffect(() => {
    if (editData) {
      setName(editData.name || "");
      setDescription(editData.description || "");
      setOwnerId(editData.owner || "");
    }
  }, [editData]);

  const handleSubmit = () => {
    const formData = {
      name: name.trim(),
      description,
    };
    if (isSuperAdmin) {
      if (ownerId) formData.owner = ownerId;
    }
    handleUpdate(editData.id, formData);
  };

  useEffect(() => {
    if (isSuccess) {
      setName("");
      setDescription("");
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir={fieldDirection}>
      <DialogTitle>{t("shop.editShop")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1, direction: fieldDirection }}>
          {isSuperAdmin && (
            <TextField
              select
              margin="dense"
              label={t("shop.ownerUser", "Select User")}
              fullWidth
              required
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              disabled={isUsersLoading}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: { direction: fieldDirection, textAlign: fieldTextAlign },
                  },
                },
              }}
              {...rtlTextFieldProps}
              sx={{ ...rtlTextFieldProps.sx, mb: 2 }}
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.username || user.email || `User ${user.id}`}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            autoFocus
            margin="dense"
            label={t("shop.shopName")}
            type="text"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            {...rtlTextFieldProps}
          />
          <TextField
            margin="dense"
            label={t("shop.description")}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            {...rtlTextFieldProps}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("shop.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !name.trim()}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("shop.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShopEdit;
